import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import {t} from 'i18next'
import {ReactComponent as LoadingSvg} from './assets/loading.svg'
import {ReactComponent as SuccessSvg} from './assets/success.svg'
import {ReactComponent as WaitingSvg} from './assets/waiting.svg'
import {styles} from './styles'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {SITE_POLLING_INTERVAL, usePollSiteById} from './hooks/usePollSiteById'
import {useJobSiteCreationProcessContext} from './JobSiteCreationProcessContext'
import {OrderIntakeSite} from '../rmc/orderIntake'
import {useSelector} from 'react-redux'
import {AppState} from '../../Root.store'
import {selectSelectedCustomerFilter} from '../../Organisms/Customers/Customers.selector'

export const JobSiteCreationLoading: React.FC<{
  onClose: () => void
  siteId: string
}> = ({onClose, siteId}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const selectedCustomer = useSelector((state: AppState) => selectSelectedCustomerFilter(state))

  const MESSAGE_HEIGHT = 32
  const ANIMATION_DURATION = 500

  const loadingMessages = useMemo(
    () => [
      t('orderIntake.rmc.jobSiteModal.loading.checkingNewAddress'),
      t('orderIntake.rmc.jobSiteModal.loading.verifyingAddressDetails'),
      t('orderIntake.rmc.jobSiteModal.loading.updatingRecords'),
      t('orderIntake.rmc.jobSiteModal.loading.synchronizingNewData'),
      t('orderIntake.rmc.jobSiteModal.loading.verifyingSystemStatus'),
      t('orderIntake.rmc.jobSiteModal.loading.almostDone')
    ],
    []
  )

  const {isSuccess, isError, data} = usePollSiteById(siteId)

  const {onCreateNewOrder} = useJobSiteCreationProcessContext()

  const goToOrderIntakeScreen = useCallback(() => {
    if (!data || isError) return
    onCreateNewOrder({
      ...data,
      customerNumber: selectedCustomer?.customerNumber ?? ''
    } as OrderIntakeSite)
  }, [isError, data, onCreateNewOrder, selectedCustomer?.customerNumber])

  useEffect(() => {
    if (loadingMessages.length <= 1 || isSuccess || isError) return

    const scheduleNextMessage = (index: number) => {
      if (index < loadingMessages.length - 1) {
        const timeout = setTimeout(() => {
          setCurrentMessageIndex(index + 1)
          scheduleNextMessage(index + 1)
        }, SITE_POLLING_INTERVAL)
        return timeout
      }
    }

    const timeout = scheduleNextMessage(currentMessageIndex)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [loadingMessages.length, currentMessageIndex, isSuccess, isError])

  const title = useMemo(() => {
    if (isSuccess) {
      return t('orderIntake.rmc.jobSiteModal.created.title')
    }
    if (isError) {
      return t('orderIntake.rmc.jobSiteModal.waiting.title')
    }
    return t('orderIntake.rmc.jobSiteModal.create.mapTitle')
  }, [isSuccess, isError])

  const description = useMemo(() => {
    if (isSuccess) {
      return t('orderIntake.rmc.jobSiteModal.created.description')
    }
    if (isError) {
      return t('orderIntake.rmc.jobSiteModal.waiting.description')
    }
    return t('orderIntake.rmc.jobSiteModal.loading.description')
  }, [isSuccess, isError])

  return (
    <>
      <DialogTitle
        sx={{...styles.dialogTitle, ...styles.mapDialogTitle}}
        data-test-id="map-dialog-title"
      >
        <Typography
          variant="h3"
          component="div"
          sx={styles.titleText}
          data-test-id="map-dialog-title-text"
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
          data-test-id="map-dialog-close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent} data-test-id="map-dialog-content">
        <Box sx={styles.loadingContainer} data-test-id="loading-container">
          <Box sx={styles.illustrationContainer}>
            {isSuccess ? (
              <SuccessSvg
                data-test-id="job-site-success-illustration"
                style={styles.illustration}
              />
            ) : isError ? (
              <WaitingSvg
                data-test-id="job-site-waiting-illustration"
                style={styles.illustration}
              />
            ) : (
              <LoadingSvg
                data-test-id="job-site-creation-illustration"
                style={styles.illustration}
              />
            )}
          </Box>
          <Box
            sx={{
              ...styles.messageContainer,
              height: MESSAGE_HEIGHT
            }}
            role="status"
            aria-live="polite"
            aria-label="Loading progress"
          >
            {isSuccess && (
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  ...styles.messageTypography,
                  height: MESSAGE_HEIGHT
                }}
              >
                {t('orderIntake.rmc.jobSiteModal.created.jobSiteAdded')}
              </Typography>
            )}
            {isError && (
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  ...styles.messageTypography,
                  height: MESSAGE_HEIGHT
                }}
              >
                {t('orderIntake.rmc.jobSiteModal.waiting.notYetAvailable')}
              </Typography>
            )}
            {!isSuccess && !isError && (
              <Box
                sx={{
                  ...styles.messagesAnimationContainer,
                  height: loadingMessages.length * MESSAGE_HEIGHT,
                  transform: `translateY(${-currentMessageIndex * MESSAGE_HEIGHT}px)`,
                  transition: `transform ${ANIMATION_DURATION}ms ease-in-out`
                }}
              >
                {loadingMessages.map((message, index) => (
                  <Typography
                    key={index}
                    variant="h4"
                    component="h2"
                    sx={{
                      ...styles.messageTypography,
                      height: MESSAGE_HEIGHT
                    }}
                    data-test-id={`validation-heading-${index}`}
                  >
                    {message}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          <Typography
            variant="body1"
            sx={styles.descriptionText}
            data-test-id="validation-subtitle"
          >
            {description}
          </Typography>

          {isSuccess && (
            <Box sx={styles.buttonContainer}>
              <Button
                variant="contained"
                fullWidth
                sx={styles.continueButton}
                onClick={goToOrderIntakeScreen}
                data-test-id="continue-order-button"
              >
                {t('orderIntake.rmc.jobSiteModal.created.continueOrder', 'Continue order')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={styles.backButton}
                onClick={onClose}
                data-test-id="back-to-jobsites-button"
              >
                {t('orderIntake.rmc.jobSiteModal.created.backToJobsites', 'Back to jobsites list')}
              </Button>
            </Box>
          )}

          {isError && (
            <Button
              variant="outlined"
              fullWidth
              sx={styles.backButtonFullWidth}
              data-test-id="back-to-orders-button-waiting"
              onClick={onClose}
            >
              {t(
                'orderIntake.rmc.jobSiteModal.waiting.backToOrders',
                'Back to Orders & Deliveries'
              )}
            </Button>
          )}

          {!isSuccess && !isError && (
            <CircularProgress
              size={48}
              thickness={3}
              sx={styles.loadingSpinner}
              data-test-id="job-site-creation-loader"
            />
          )}
        </Box>
      </DialogContent>
    </>
  )
}
